import React from 'react';
import { DatePickerInput } from '@mantine/dates';
import { Icon } from '../../assets/icons/Icon';
import styles from './DateSelector.module.scss';

interface DateSelectorProps {
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  placeholder?: string;
  recentLogs?: Array<{ log_date: string }>;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  onDateChange,
  placeholder = "Select date",
  recentLogs = []
}) => {
  const formatDisplayDate = (dateString: string | null): string => {
    if (!dateString) return placeholder;
    
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return `Today, ${date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long'
      })}`;
    }
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };


  // Date tracking logic (same as WeeklyCalendar)
  const canTrackDate = (date: Date): boolean => {
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff >= 0 && daysDiff <= 2; // Can track today and up to 2 days back
  };

  const isDateCompleted = (date: Date): boolean => {
    const dateString = date.getFullYear() + '-' + 
      String(date.getMonth() + 1).padStart(2, '0') + '-' + 
      String(date.getDate()).padStart(2, '0');
    return recentLogs.some(log => log.log_date === dateString);
  };

  const isSelectedDateLogged = (): boolean => {
    if (!selectedDate) return false;
    const date = new Date(selectedDate);
    return isDateCompleted(date);
  };

  const handleDateSelect = (value: string | null) => {
    if (!value) {
      onDateChange(null);
      return;
    }

    const date = new Date(value);
    // Only allow selection of trackable dates
    if (canTrackDate(date)) {
      onDateChange(value);
    }
  };

  // Custom day renderer to show dots for completed dates
  const renderDay = (dateString: string) => {
    const date = new Date(dateString);
    const isCompleted = isDateCompleted(date);
    
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {date.getDate()}
        {isCompleted && (
          <div
            style={{
              position: 'absolute',
              bottom: '2px',
              right: '2px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: '#4c6ef5',
            }}
          />
        )}
      </div>
    );
  };

  const DropdownArrow = () => (
    <svg
      width="12"
      height="8"
      viewBox="0 0 12 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color: '#495057' }}
    >
      <path
        d="M1 1.5L6 6.5L11 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const RightSection = () => {
    if (isSelectedDateLogged()) {
      return <Icon iconName="BlueCheckedIcon" width="1.25rem" height="1.25rem" />;
    }
    return null;
  };

  return (
    <div className={styles.dateSelectorWrapper}>
      <DatePickerInput
        value={selectedDate}
        onChange={handleDateSelect}
        size="sm"
        firstDayOfWeek={1} // Monday
        hideOutsideDates
        placeholder={formatDisplayDate(selectedDate)}
        leftSection={<RightSection />}
        rightSection={<DropdownArrow />}
        rightSectionPointerEvents="none"
        renderDay={renderDay}
        getDayProps={(dateString) => {
          const date = new Date(dateString);
          const canTrack = canTrackDate(date);
          return {
            disabled: !canTrack,
            style: {
              opacity: canTrack ? 1 : 0.3,
              cursor: canTrack ? 'pointer' : 'not-allowed',
            }
          };
        }}
        popoverProps={{
          withinPortal: true,
          zIndex: 2000,
        }}
        minDate={(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; })()}
        maxDate={new Date().toISOString().split('T')[0]}
        styles={{
          day: {
            borderRadius: '50%',
            height: '2rem',
            width: '2rem',
            fontSize: '0.875rem',
          },
        }}
      />
    </div>
  );
};
